// src/pages/admin/AdminBooks.tsx
import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, setDoc, deleteDoc, orderBy, query } from 'firebase/firestore';
import { ref as sRef, getDownloadURL } from 'firebase/storage';
import { db, storage, uploadFileWithToken } from '../../lib/firebase';
import { clearBookCache } from '../../services/exerciseService';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';

interface BookDoc {
  id:           string;
  title:        string;
  description:  string;
  coverUrl:     string;
  displayOrder: number;
  status:       'live' | 'draft';
  units:        number;
  level:        string;
  tier:         'free' | 'premium';
  unitTitles:   string;   // comma-separated for editing
  exercisesJsonUrl: string;
}

const EMPTY_FORM: Omit<BookDoc, 'id'> = {
  title: '', description: '', coverUrl: '', displayOrder: 0,
  status: 'draft', units: 1, level: '', tier: 'premium',
  unitTitles: '', exercisesJsonUrl: '',
};

function toBookDoc(id: string, data: Record<string, unknown>): BookDoc {
  return {
    id,
    title:            (data.title          as string)  ?? '',
    description:      (data.description    as string)  ?? '',
    coverUrl:         (data.coverUrl       as string)  ?? '',
    displayOrder:     (data.displayOrder   as number)  ?? 0,
    status:           (data.status         as 'live' | 'draft') ?? 'draft',
    units:            (data.units          as number)  ?? 1,
    level:            (data.level          as string)  ?? '',
    tier:             (data.tier           as 'free' | 'premium') ?? 'premium',
    unitTitles:       Array.isArray(data.unitTitles) ? (data.unitTitles as string[]).join(', ') : ((data.unitTitles as string) ?? ''),
    exercisesJsonUrl: (data.exercisesJsonUrl as string) ?? '',
  };
}

const inputStyle = (C: ReturnType<typeof useThemePalette>, isDark: boolean): React.CSSProperties => ({
  padding: '9px 12px', borderRadius: '10px', width: '100%', boxSizing: 'border-box',
  border: `1px solid ${C.border}`,
  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  color: C.text, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none',
});

const labelStyle = (C: ReturnType<typeof useThemePalette>): React.CSSProperties => ({
  fontSize: '0.72rem', fontWeight: 700, color: C.muted, display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em',
});

export default function AdminBooks() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [books,      setBooks]      = useState<BookDoc[]>([]);
  const [editing,    setEditing]    = useState<BookDoc | null>(null);
  const [form,       setForm]       = useState<Omit<BookDoc, 'id'>>(EMPTY_FORM);
  const [coverFile,  setCoverFile]  = useState<File | null>(null);
  const [jsonFile,   setJsonFile]   = useState<File | null>(null);
  const [showForm,   setShowForm]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [status,     setStatus]     = useState('');
  const [isErr,      setIsErr]      = useState(false);
  const [delConfirm, setDelConfirm] = useState<string | null>(null);

  const load = () => {
    getDocs(query(collection(db, 'books'), orderBy('displayOrder')))
      .then(snap => {
        const list: BookDoc[] = [];
        snap.forEach(d => list.push(toBookDoc(d.id, d.data())));
        setBooks(list);
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, displayOrder: books.length + 1 });
    setCoverFile(null);
    setJsonFile(null);
    setStatus('');
    setShowForm(true);
  };

  const openEdit = (book: BookDoc) => {
    setEditing(book);
    setForm({
      title: book.title, description: book.description, coverUrl: book.coverUrl,
      displayOrder: book.displayOrder, status: book.status, units: book.units,
      level: book.level, tier: book.tier, unitTitles: book.unitTitles, exercisesJsonUrl: book.exercisesJsonUrl,
    });
    setCoverFile(null);
    setJsonFile(null);
    setStatus('');
    setShowForm(true);
  };

  const slugify = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `book${Date.now()}`;

  const save = async () => {
    if (!form.title.trim()) { setStatus('Title is required'); setIsErr(true); return; }
    setSaving(true); setStatus('Saving...'); setIsErr(false);

    try {
      const bookId = editing?.id ?? slugify(form.title);
      let coverUrl       = form.coverUrl;
      let exercisesJsonUrl = form.exercisesJsonUrl;

      if (coverFile) {
        const ext = coverFile.name.split('.').pop() ?? 'jpg';
        const storageRef = sRef(storage, `covers/${bookId}.${ext}`);
        await uploadFileWithToken(storageRef, coverFile);
        coverUrl = await getDownloadURL(storageRef);
      }

      if (jsonFile) {
        const storageRef = sRef(storage, `exercises/${bookId}/exercises.json`);
        await uploadFileWithToken(storageRef, jsonFile);
        exercisesJsonUrl = await getDownloadURL(storageRef);
        clearBookCache();
      }

      const unitTitlesArr = form.unitTitles
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const data = {
        title:            form.title,
        description:      form.description,
        coverUrl,
        displayOrder:     Number(form.displayOrder),
        status:           form.status,
        units:            Number(form.units),
        level:            form.level,
        tier:             form.tier,
        unitTitles:       unitTitlesArr.length > 0 ? unitTitlesArr : null,
        exercisesJsonUrl,
      };

      await setDoc(doc(db, 'books', bookId), data, { merge: true });

      setStatus('Saved!'); setIsErr(false);
      setTimeout(() => { setStatus(''); setShowForm(false); load(); }, 1200);
    } catch (e: unknown) {
      setStatus(e instanceof Error ? e.message : 'Save failed'); setIsErr(true);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'books', id));
      setBooks(prev => prev.filter(b => b.id !== id));
      setDelConfirm(null);
    } catch {
      setStatus('Delete failed'); setIsErr(true);
    }
  };

  const field = (key: keyof Omit<BookDoc, 'id'>, value: string | number) =>
    setForm(f => ({ ...f, [key]: value }));

  return (
    <div style={{ padding: '32px', maxWidth: '960px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text, marginBottom: '4px' }}>Manage Books</div>
          <div style={{ fontSize: '0.8rem', color: C.muted }}>Add, edit, or remove books</div>
        </div>
        <button
          onClick={openAdd}
          style={{ padding: '9px 20px', borderRadius: '999px', border: 'none', background: C.accent, color: '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          + Add Book
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.borderH}`, borderRadius: '18px', padding: '28px', marginBottom: '32px' }}>
          <div style={{ fontSize: '0.92rem', fontWeight: 800, color: C.text, marginBottom: '20px' }}>
            {editing ? `Edit: ${editing.title}` : 'New Book'}
            {!editing && <span style={{ fontSize: '0.72rem', color: C.muted, marginLeft: '8px', fontWeight: 400 }}>ID auto-generated from title</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={labelStyle(C)}>Title *</label>
              <input value={form.title} onChange={e => field('title', e.target.value)} style={inputStyle(C, isDark)} />
            </div>
            <div>
              <label style={labelStyle(C)}>Display Order</label>
              <input type="number" value={form.displayOrder} onChange={e => field('displayOrder', e.target.value)} style={inputStyle(C, isDark)} />
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle(C)}>Description</label>
            <textarea value={form.description} onChange={e => field('description', e.target.value)} rows={2} style={{ ...inputStyle(C, isDark), resize: 'vertical' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={labelStyle(C)}>Status</label>
              <select value={form.status} onChange={e => field('status', e.target.value)} style={inputStyle(C, isDark)}>
                <option value="live">Live</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div>
              <label style={labelStyle(C)}>Units</label>
              <input type="number" min={1} value={form.units} onChange={e => field('units', e.target.value)} style={inputStyle(C, isDark)} />
            </div>
            <div>
              <label style={labelStyle(C)}>Level</label>
              <input value={form.level} onChange={e => field('level', e.target.value)} placeholder="e.g. B1–B2" style={inputStyle(C, isDark)} />
            </div>
            <div>
              <label style={labelStyle(C)}>Tier</label>
              <select value={form.tier} onChange={e => field('tier', e.target.value)} style={inputStyle(C, isDark)}>
                <option value="free">Free</option>
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle(C)}>Unit Titles (comma-separated)</label>
            <input value={form.unitTitles} onChange={e => field('unitTitles', e.target.value)} placeholder="Unit 1 Title, Unit 2 Title, ..." style={inputStyle(C, isDark)} />
          </div>

          {/* File uploads */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
            <div style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: '12px', padding: '14px', border: `1px solid ${C.border}` }}>
              <label style={labelStyle(C)}>Cover Image {form.coverUrl && <span style={{ color: C.success }}>✓ exists</span>}</label>
              <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files?.[0] ?? null)} style={{ fontSize: '0.8rem', color: C.text }} />
              {coverFile && <div style={{ fontSize: '0.72rem', color: C.premium, marginTop: '5px' }}>{coverFile.name}</div>}
            </div>
            <div style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: '12px', padding: '14px', border: `1px solid ${C.border}` }}>
              <label style={labelStyle(C)}>Exercises JSON {form.exercisesJsonUrl && <span style={{ color: C.success }}>✓ exists</span>}</label>
              <input type="file" accept="application/json,.json" onChange={e => setJsonFile(e.target.files?.[0] ?? null)} style={{ fontSize: '0.8rem', color: C.text }} />
              {jsonFile && <div style={{ fontSize: '0.72rem', color: C.premium, marginTop: '5px' }}>{jsonFile.name}</div>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={() => void save()} disabled={saving} style={{ padding: '9px 22px', borderRadius: '999px', border: 'none', background: C.accent, color: '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              {saving ? 'Saving...' : 'Save Book'}
            </button>
            <button onClick={() => setShowForm(false)} style={{ padding: '9px 18px', borderRadius: '999px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            {status && <span style={{ fontSize: '0.78rem', color: isErr ? C.error : C.success }}>{status}</span>}
          </div>
        </div>
      )}

      {/* Book list table */}
      {books.length === 0 ? (
        <div style={{ color: C.muted, fontSize: '0.85rem' }}>No books yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {books.map(book => (
            <div key={book.id} style={{
              display: 'flex', alignItems: 'center', gap: '14px',
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: '14px', padding: '14px 18px',
            }}>
              {book.coverUrl && (
                <img src={book.coverUrl} alt={book.title} style={{ width: 44, height: 56, objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 700, color: C.text }}>{book.title}</span>
                  <span style={{
                    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                    padding: '2px 8px', borderRadius: '999px',
                    color: book.status === 'live' ? C.success : C.premium,
                    background: book.status === 'live' ? 'rgba(52,211,153,0.1)' : 'rgba(251,191,36,0.08)',
                    border: `1px solid ${book.status === 'live' ? 'rgba(52,211,153,0.25)' : 'rgba(251,191,36,0.2)'}`,
                  }}>
                    {book.status}
                  </span>
                  <span style={{ fontSize: '0.6rem', color: C.muted }}>#{book.id}</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '3px' }}>
                  {book.units} units · {book.tier} · {book.level || 'No level'}
                  {book.exercisesJsonUrl && <span style={{ color: C.success, marginLeft: '8px' }}>✓ exercises</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => openEdit(book)} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Edit
                </button>
                {delConfirm === book.id ? (
                  <button onClick={() => void remove(book.id)} style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.1)', color: C.error, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Sure?
                  </button>
                ) : (
                  <button onClick={() => setDelConfirm(book.id)} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
